import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';

/**
 * The dispatch board's theme.
 *
 * 🚨 IT DOES NOT EXTEND THE APP'S THEME, and that is deliberate twice over.
 *
 * First, it does not need to: `createTheme` appears nowhere else in this codebase, so every
 * other page already renders on MUI's defaults. Starting from those defaults IS matching them.
 *
 * Second, it cannot. `createTheme(appTheme, options)` deep-merges two very large types, and the
 * TypeScript checker ran out of memory doing it — the build died with "Reached heap limit" at
 * 6 GB, with no error message anyone could act on. Annotating the return type and casting the
 * options made no difference; only dropping the two-argument form did.
 *
 * ⚠️ If the admin panel ever gains a theme of its own, inherit the few fields that matter
 * (fontFamily, shape) by naming them here. Do not reach for the two-argument form again.
 *
 * 🚨 NO MODULE AUGMENTATION. An earlier version added `rider`, `hold`, `late` and `sched` to
 * MUI's Palette interface. Every `sx` colour lookup on the board then had to resolve against
 * the widened type, and with roughly sixty of them the TypeScript checker ran out of memory
 * and the build died with "Reached heap limit" — at 6 GB, with no error anyone could read.
 *
 * MUI's own semantic slots carry the same meanings, so the board borrows them instead:
 *
 *     primary   → the rider accent (order ids, active tour, the current stop)
 *     warning   → held back, assigned, "nearly out of slot"
 *     error     → late and overdue
 *     secondary → parked and reassignments
 *     success   → delivered, on time, the live pulse
 *
 * Overriding them is safe because this theme is scoped to a ThemeProvider around the board
 * alone; no other page sees it.
 */

/**
 * ⚠️ The semantic hues are DARKENED for daylight rather than reused. An inversion alone leaves
 * red and green washed out on white, and those two carry "late" and "fine" — the two readings
 * a dispatcher takes from across the room.
 *
 * 🚨 THE WALL DISPLAY SIZES ITSELF TO THE VIEWPORT — §1.1, "all sizing is in vw, so 1080p and
 * 4K render identically". Every size here is a clamp whose MINIMUM is what the page already
 * uses, so nothing shrinks and nothing moves on a 1920-wide screen: 0.73vw is 14px there, which
 * is exactly `body2`. Wider than that and it grows; narrower and the minimum holds.
 *
 * ⚠️ NOT THE OLD FIXED BUMP, which multiplied everything by a constant and broke the board the
 * other way — the rail wrapped onto four lines and three stops filled a card that had held
 * eight. A clamp cannot do that, because it never outgrows the screen it is measuring.
 *
 * ⚠️ Spacing and padding stay in pixels deliberately. Scaling those as well turns one wrapped
 * address into a card that holds two rows. The row fitter measures whatever height results.
 */
const TV_TYPOGRAPHY = {
  fontSize: 14,
  h5: { fontSize: 'clamp(1.5rem, 1.25vw, 3rem)' },
  h6: { fontSize: 'clamp(1.25rem, 1.04vw, 2.5rem)' },
  subtitle1: { fontSize: 'clamp(1rem, 0.83vw, 2rem)' },
  subtitle2: { fontSize: 'clamp(0.875rem, 0.73vw, 1.75rem)' },
  body1: { fontSize: 'clamp(1rem, 0.83vw, 2rem)' },
  body2: { fontSize: 'clamp(0.875rem, 0.73vw, 1.75rem)' },
  caption: { fontSize: 'clamp(0.75rem, 0.625vw, 1.5rem)' },
};

export const boardTheme = (daylight: boolean, tv = false): Theme =>
  createTheme({
    ...(tv ? { typography: TV_TYPOGRAPHY } : {}),
    palette: {
      mode: daylight ? 'light' : 'dark',
      background: {
        default: daylight ? '#FFFFFF' : '#12161A',
        paper: daylight ? '#FFFFFF' : '#171C21',
      },
      divider: daylight ? '#E2E8F0' : '#2A333B',
      primary: { main: daylight ? '#1657B8' : '#8AB4FF' },
      secondary: { main: daylight ? '#5B32A8' : '#C4A3FF' },
      warning: { main: daylight ? '#9A5B00' : '#F5B342' },
      error: { main: daylight ? '#C62828' : '#FF6B6B' },
      success: { main: daylight ? '#1B7F4B' : '#4ADE80' },
    },
    components: {
      MuiChip: { defaultProps: { size: 'small' } },
    },
  } as ThemeOptions);
